'use client';

import { useState } from 'react';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

type ConfirmDialogProps = {
    open: boolean;
    onClose: () => void;

    /** Headline of the dialog */
    title: React.ReactNode;

    /** Main body text (can be JSX) */
    description?: React.ReactNode;

    /** Label for the confirm button (default: "Delete") */
    confirmText?: string;

    /** Label for the cancel button (default: "Cancel") */
    cancelText?: string;

    /** Called when user confirms. Supports async; modal locks while pending. */
    onConfirm: () => void | Promise<void>;

    /** Visual tone; "danger" renders red confirm button */
    tone?: 'danger' | 'default';

    /** Hide the red warning icon (default: false) */
    hideIcon?: boolean;
};

export default function ConfirmDialog({
                                          open,
                                          onClose,
                                          title,
                                          description,
                                          confirmText = 'Delete',
                                          cancelText = 'Cancel',
                                          onConfirm,
                                          tone = 'danger',
                                          hideIcon = false,
                                      }: ConfirmDialogProps) {
    const [submitting, setSubmitting] = useState(false);

    const handleConfirm = async () => {
        try {
            setSubmitting(true);
            await onConfirm();
            onClose();
        } finally {
            setSubmitting(false);
        }
    };

    // Prevent closing while submitting
    const safeClose = () => {
        if (!submitting) onClose();
    };

    const confirmBtnClasses =
        tone === 'danger'
            ? 'bg-red-600 hover:bg-red-500 text-white'
            : 'bg-blue-900 hover:bg-blue-800 text-white';

    return (
        <Dialog open={open} onClose={safeClose} className="relative z-50">
            <DialogBackdrop
                transition
                className="fixed inset-0 bg-gray-800/75 data-[closed]:opacity-0 data-[enter]:duration-300 data-[enter]:ease-out data-[leave]:duration-200 data-[leave]:ease-in"
            />
            <div className="fixed inset-0 z-50 w-screen overflow-y-auto">
                <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <DialogPanel
                        transition
                        className="relative w-full max-w-lg transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all
                       data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[enter]:ease-out data-[leave]:duration-200 data-[leave]:ease-in
                       sm:my-8 sm:p-6 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
                    >
                        <div className="sm:flex sm:items-start">
                            {!hideIcon && (
                                <div className="mx-auto flex size-12 shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:size-10">
                                    <ExclamationTriangleIcon aria-hidden="true" className="size-6 text-red-600" />
                                </div>
                            )}
                            <div className={`mt-3 ${hideIcon ? '' : 'sm:ml-4'} sm:mt-0 text-center sm:text-left`}>
                                <DialogTitle as="h3" className="text-base font-semibold text-blue-900">
                                    {title}
                                </DialogTitle>
                                {description && <div className="mt-2 text-sm text-gray-600">{description}</div>}
                            </div>
                        </div>

                        <div className={`mt-5 sm:mt-6 ${hideIcon ? '' : 'sm:ml-10'} sm:flex sm:pl-4 sm:justify-end gap-3`}>
                            <button
                                type="button"
                                data-autofocus
                                onClick={safeClose}
                                disabled={submitting}
                                className="mt-3 sm:mt-0 inline-flex w-full sm:w-auto justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-blue-900 shadow-xs ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-60"
                            >
                                {cancelText}
                            </button>
                            <button
                                type="button"
                                onClick={handleConfirm}
                                disabled={submitting}
                                className={`inline-flex w-full sm:w-auto justify-center rounded-md px-3 py-2 text-sm font-semibold shadow-xs disabled:opacity-60 ${confirmBtnClasses}`}
                            >
                                {submitting ? 'Working…' : confirmText}
                            </button>
                        </div>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    );
}
